/**
 * COMPONENT: SettingsExtras.jsx
 * Pilcore-style management surface for extras and discounts.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import Select from "react-select";
import { useSearchParams } from "react-router-dom";
import axios from "../api/axiosInstance";
import { usePmsTheme } from "./PmsThemeContext";
import { readStoredUser } from "../utils/sessionUser";
import { useCurrencyDisplay } from "../currency/CurrencyDisplayContext";
import {
  currencyAmountStep,
  formatCurrencyAmount,
  getCurrencyMinorUnits,
  hasValidCurrencyPrecision,
} from "../currency/currencyPrecision";
import { DEMO_BUSINESS_UNIT_ID, isDemoMode, readDemoUser } from "../demo/demoMode";
import {
  SETTINGS_EXTRAS_BUSINESS_UNIT_ERRORS,
  resolveSettingsExtrasBusinessUnit,
} from "./settingsExtrasBusinessUnit";
import {
  SETTINGS_EXTRA_AMOUNT_VALIDATION,
  getSettingsExtraAmountValidation,
} from "./settingsExtrasValidation";
import "./SettingsExtras.css";

const SETTINGS_EXTRAS_MESSAGES = {
  en: {
    header: {
      kicker: "Settings cockpit",
      title: "Extras & Discounts",
      subtitle:
        "Create surcharges, discounts and mandatory add-ons with pricing logic, validity windows and inventory targeting.",
    },
    actions: {
      refresh: "Refresh",
      create: "New extra",
      edit: "Edit",
      delete: "Delete",
      deactivate: "Deactivate",
      activate: "Activate",
      cancel: "Cancel",
      save: "Save extra",
      deleting: "Deleting...",
      close: "Close modal",
      viewOnly: "View only",
    },
    kpis: {
      totalLabel: "Configured",
      totalFoot: "Total extras and discounts",
      automaticLabel: "Automatic",
      automaticFoot: "Applied without manual action",
      mandatoryLabel: "Mandatory",
      mandatoryFoot: "Required during booking flow",
      inclusiveLabel: "Inclusive",
      inclusiveFoot: "Already included in room rate",
      validityLabel: "Dated",
      validityFoot: "Linked to a validity window",
    },
    filters: {
      scopeLabel: "Scope filter",
      quickViewLabel: "Quick view",
      searchPlaceholder: "Search by name, description, category or rate...",
      allScopes: "All scopes",
      allRecords: "All records",
      automaticOnly: "Automatic only",
      mandatoryOnly: "Mandatory only",
      inclusiveOnly: "Inclusive only",
      datedOnly: "With validity",
    },
    states: {
      loadingTitle: "Loading extras...",
      loadingText: "Fetching extras, room categories and room rates.",
      loadErrorTitle: "Could not load extras",
      loadErrorText: "Try refreshing the panel.",
      businessUnitErrorTitle: "Business unit unavailable",
      businessUnitMissingText: "Select an active business unit before managing extras.",
      businessUnitInvalidText: "The business_unit_id in the URL is not valid.",
      businessUnitMismatchText:
        "This page belongs to another business unit. Switch to that business unit or reopen Extras from Settings.",
      emptyTitle: "No extras found",
      emptyText: "Create your first extra or relax the active filters.",
    },
    table: {
      extra: "Extra",
      pricing: "Pricing",
      scope: "Scope",
      targeting: "Targeting",
      validity: "Validity",
      flags: "Flags",
      actions: "Actions",
      roomCategories: "Room categories",
      roomRates: "Room rates",
      allInventory: "All inventory",
      allCategories: "All room categories",
      allRates: "All room rates",
      noDescription: "No description",
      noValidity: "No validity window",
      nights: "Nights",
      pax: "Pax",
      automatic: "Automatic",
      mandatory: "Mandatory",
      inclusive: "Inclusive",
      bookingEngine: "Booking engine",
      none: "None",
      from: "From",
      until: "Until",
    },
    modal: {
      kicker: "Extras setup",
      createTitle: "Create extra or discount",
      editTitle: "Edit extra or discount",
      subtitle: "Define the commercial rule, operational scope and targeting in one place.",
      otaHint:
        "If this extra will also exist in an OTA, use exactly the same name and matching charge conditions so the automatic mapping can recognize it.",
      sections: {
        commercialTitle: "Commercial setup",
        commercialText: "Name, amount model and short operational description.",
        logicTitle: "Charge logic",
        logicText: "Decide the reservation scope and how the amount is charged.",
        targetingTitle: "Inventory targeting",
        targetingText:
          "Include or exclude specific room categories and room rates. Empty selections leave that dimension unrestricted.",
        restrictionsTitle: "Restrictions",
        restrictionsText: "Optional stay or occupancy boundaries for this rule.",
        switchesTitle: "Behaviour",
        switchesText:
          "Control booking-engine visibility and whether the extra is inclusive, automatic or mandatory.",
        summaryTitle: "Live summary",
        summaryText: "Preview the rule before saving it.",
        targetsTitle: "Selected targeting",
        targetsText: "Leave both selectors empty to apply the rule across all inventory.",
      },
      help: {
        reservationScope: "Group reservation scope ignores charge unit and pax limits.",
        roomScope: "Per-room scope can also charge by adult, child or pax.",
        currency:
          "Fixed amounts may use any enabled currency; the server converts them using the configured rate. Percentage amounts are currency-independent.",
        referencesLoading: "Loading room categories and room rates...",
        referencesError: "Room categories or room rates could not be loaded.",
      },
    },
    fields: {
      name: "Name",
      amount: "Amount",
      amountType: "Amount type",
      currency: "Currency",
      scope: "Scope",
      chargePeriod: "Charge period",
      chargeUnit: "Charge unit",
      validFrom: "Valid from",
      validTo: "Valid to",
      roomCategories: "Room categories",
      roomRates: "Room rates",
      minNights: "Min nights",
      maxNights: "Max nights",
      minPax: "Min pax",
      maxPax: "Max pax",
      description: "Description",
      inclusive: "Inclusive (already in rate)",
      automatic: "Apply automatically",
      mandatory: "Mandatory",
      bookingEngineVisible: "Visible in booking engine",
      bookingEngineVisibleHint:
        "Guests can select this extra in direct booking flows. Inactive extras are never shown.",
      roomCategoryTargetingMode: "Category rule",
      roomRateTargetingMode: "Rate rule",
    },
    placeholders: {
      name: "Breakfast supplement",
      description: "Internal description for the team",
      roomCategories: "Select one or more categories",
      roomRates: "Select one or more rates",
      noOptions: "No matching options",
      noCode: "no code",
    },
    options: {
      scope: {
        per_room: "Per individual reservation",
        per_reservation: "Per group reservation",
      },
      chargePeriod: {
        per_stay: "Per stay",
        per_night: "Per night",
      },
      chargeUnit: {
        per_room: "Per room",
        per_adult: "Per adult",
        per_child: "Per child",
        per_pax: "Per pax",
      },
      amountType: {
        absolute: "Fixed amount",
        relative: "Relative (%)",
      },
      targetingMode: {
        include: "Include only",
        exclude: "All except",
      },
      scopeFilter: {
        all: "All scopes",
        per_room: "Per room",
        per_reservation: "Per reservation",
      },
      quickView: {
        all: "All records",
        automatic: "Automatic only",
        mandatory: "Mandatory only",
        inclusive: "Inclusive only",
        dated: "With validity",
      },
    },
    summary: {
      amount: "Amount",
      charge: "Charge",
      scope: "Scope",
      validity: "Validity",
      restrictions: "Restrictions",
      flags: "Flags",
      connection: "OTA status",
      linked: "Linked to OTAs",
      notLinked: "Not linked to OTAs yet",
      categories: "Categories",
      rates: "Rates",
      allInventory: "Applies to all categories and rates",
      includeOnly: "Include only",
      allExcept: "All except",
      noFlags: "No special flags",
      noRestrictions: "No stay or pax limits",
    },
    messages: {
      loadError: "Could not load extras.",
      referencesError: "Could not load room categories or room rates.",
      saveError: "Could not save the extra.",
      deleteError: "Could not delete the extra.",
      toggleError: "Could not change the extra status.",
      saveSuccess: "Extra saved.",
      deleteSuccess: "Extra deleted.",
      deactivateSuccess: "Extra deactivated.",
      activateSuccess: "Extra activated.",
      validationName: "Name is required.",
      validationAmount: "Amount is required.",
      validationAmountNonZero: "Amount must not be zero.",
      validationMinorUnits: (currency, minorUnits) =>
        `${currency} accepts ${minorUnits === 0 ? "whole units only" : `up to ${minorUnits} decimal places`}.`,
      confirmDelete: (name) => `Delete ${name}?`,
      deleteDialogHelp: "This removes the commercial rule permanently. This action cannot be undone.",
      otaLinkedTooltip: () => "Linked to OTAs.",
      otaNotLinkedTooltip:
        "Not linked to any OTA yet. Use the exact same name and matching charge conditions if you want automatic mapping.",
    },
  },
  es: {
    header: {
      kicker: "Cabina de settings",
      title: "Extras y descuentos",
      subtitle:
        "Crea suplementos, descuentos y cargos obligatorios con logica de precio, validez y segmentacion de inventario.",
    },
    actions: {
      refresh: "Refrescar",
      create: "Nuevo extra",
      edit: "Editar",
      delete: "Eliminar",
      deactivate: "Desactivar",
      activate: "Activar",
      cancel: "Cancelar",
      save: "Guardar extra",
      deleting: "Eliminando...",
      close: "Cerrar modal",
      viewOnly: "Solo vista",
    },
    kpis: {
      totalLabel: "Configurados",
      totalFoot: "Total de extras y descuentos",
      automaticLabel: "Automaticos",
      automaticFoot: "Se aplican sin accion manual",
      mandatoryLabel: "Obligatorios",
      mandatoryFoot: "Requeridos en el flujo de reserva",
      inclusiveLabel: "Inclusivos",
      inclusiveFoot: "Ya incluidos en la tarifa",
      validityLabel: "Con validez",
      validityFoot: "Ligados a una ventana de fechas",
    },
    filters: {
      scopeLabel: "Filtro de scope",
      quickViewLabel: "Vista rapida",
      searchPlaceholder: "Buscar por nombre, descripcion, categoria o tarifa...",
      allScopes: "Todos los scopes",
      allRecords: "Todos los registros",
      automaticOnly: "Solo automaticos",
      mandatoryOnly: "Solo obligatorios",
      inclusiveOnly: "Solo inclusivos",
      datedOnly: "Con validez",
    },
    states: {
      loadingTitle: "Cargando extras...",
      loadingText: "Obteniendo extras, categorias y tarifas.",
      loadErrorTitle: "No se pudieron cargar los extras",
      loadErrorText: "Prueba a refrescar el panel.",
      businessUnitErrorTitle: "Unidad de negocio no disponible",
      businessUnitMissingText: "Selecciona una unidad de negocio activa antes de gestionar extras.",
      businessUnitInvalidText: "El business_unit_id de la URL no es valido.",
      businessUnitMismatchText:
        "Esta pagina pertenece a otra unidad de negocio. Cambia a esa unidad o vuelve a abrir Extras desde Settings.",
      emptyTitle: "No hay extras",
      emptyText: "Crea el primer extra o afloja los filtros activos.",
    },
    table: {
      extra: "Extra",
      pricing: "Precio",
      scope: "Scope",
      targeting: "Segmentacion",
      validity: "Validez",
      flags: "Flags",
      actions: "Acciones",
      roomCategories: "Categorias",
      roomRates: "Tarifas",
      allInventory: "Todo el inventario",
      allCategories: "Todas las categorias",
      allRates: "Todas las tarifas",
      noDescription: "Sin descripcion",
      noValidity: "Sin ventana de validez",
      nights: "Noches",
      pax: "Pax",
      automatic: "Automatico",
      mandatory: "Obligatorio",
      inclusive: "Inclusivo",
      bookingEngine: "Booking engine",
      none: "Ninguno",
      from: "Desde",
      until: "Hasta",
    },
    modal: {
      kicker: "Configuracion de extras",
      createTitle: "Crear extra o descuento",
      editTitle: "Editar extra o descuento",
      subtitle: "Define en un solo sitio la regla comercial, el alcance operativo y la segmentacion.",
      otaHint:
        "Si este extra tambien va a existir en una OTA, usa exactamente el mismo nombre y las mismas condiciones de cobro para que el mapeo automatico lo reconozca.",
      sections: {
        commercialTitle: "Configuracion comercial",
        commercialText: "Nombre, modelo de importe y descripcion corta para operaciones.",
        logicTitle: "Logica de cobro",
        logicText: "Decide el scope de la reserva y como se cobra el importe.",
        targetingTitle: "Segmentacion de inventario",
        targetingText:
          "Incluye o excluye categorias y tarifas concretas. Un selector vacio no limita esa dimension.",
        restrictionsTitle: "Restricciones",
        restrictionsText: "Limites opcionales de estancia u ocupacion para la regla.",
        switchesTitle: "Comportamiento",
        switchesText:
          "Controla la visibilidad en el booking engine y si el extra es inclusivo, automatico u obligatorio.",
        summaryTitle: "Resumen en vivo",
        summaryText: "Vista previa de la regla antes de guardarla.",
        targetsTitle: "Segmentacion seleccionada",
        targetsText: "Si dejas ambos selectores vacios, la regla aplicara a todo el inventario.",
      },
      help: {
        reservationScope: "El scope de reserva de grupo ignora la unidad de cobro y los limites de pax.",
        roomScope: "El scope por reserva individual tambien puede cobrar por adulto, nino o pax.",
        currency:
          "Los importes fijos pueden usar cualquier moneda habilitada; el servidor los convierte con la tasa configurada. Los porcentajes son independientes de la moneda.",
        referencesLoading: "Cargando categorias y tarifas...",
        referencesError: "No se pudieron cargar las categorias o tarifas.",
      },
    },
    fields: {
      name: "Nombre",
      amount: "Importe",
      amountType: "Tipo de importe",
      currency: "Moneda",
      scope: "Scope",
      chargePeriod: "Periodo de cobro",
      chargeUnit: "Unidad de cobro",
      validFrom: "Valido desde",
      validTo: "Valido hasta",
      roomCategories: "Categorias",
      roomRates: "Tarifas",
      minNights: "Noches min",
      maxNights: "Noches max",
      minPax: "Pax min",
      maxPax: "Pax max",
      description: "Descripcion",
      inclusive: "Inclusivo (ya incluido en tarifa)",
      automatic: "Aplicar automaticamente",
      mandatory: "Obligatorio",
      bookingEngineVisible: "Visible en el booking engine",
      bookingEngineVisibleHint:
        "El huesped podra seleccionar este extra en la reserva directa. Los extras inactivos nunca se muestran.",
      roomCategoryTargetingMode: "Regla de categorias",
      roomRateTargetingMode: "Regla de tarifas",
    },
    placeholders: {
      name: "Suplemento de desayuno",
      description: "Descripcion interna para el equipo",
      roomCategories: "Selecciona una o varias categorias",
      roomRates: "Selecciona una o varias tarifas",
      noOptions: "No hay opciones coincidentes",
      noCode: "sin codigo",
    },
    options: {
      scope: {
        per_room: "Por reserva individual",
        per_reservation: "Por reserva de grupo",
      },
      chargePeriod: {
        per_stay: "Por estancia",
        per_night: "Por noche",
      },
      chargeUnit: {
        per_room: "Por habitacion",
        per_adult: "Por adulto",
        per_child: "Por nino",
        per_pax: "Por pax",
      },
      amountType: {
        absolute: "Importe fijo",
        relative: "Relativo (%)",
      },
      targetingMode: {
        include: "Solo las seleccionadas",
        exclude: "Todas excepto",
      },
      scopeFilter: {
        all: "Todos los scopes",
        per_room: "Por reserva individual",
        per_reservation: "Por reserva de grupo",
      },
      quickView: {
        all: "Todos los registros",
        automatic: "Solo automaticos",
        mandatory: "Solo obligatorios",
        inclusive: "Solo inclusivos",
        dated: "Con validez",
      },
    },
    summary: {
      amount: "Importe",
      charge: "Cobro",
      scope: "Scope",
      validity: "Validez",
      restrictions: "Restricciones",
      flags: "Flags",
      connection: "Estado OTA",
      linked: "Vinculado a OTAs",
      notLinked: "Aun no vinculado a OTAs",
      categories: "Categorias",
      rates: "Tarifas",
      allInventory: "Aplica a todas las categorias y tarifas",
      includeOnly: "Solo seleccionadas",
      allExcept: "Todas excepto",
      noFlags: "Sin flags especiales",
      noRestrictions: "Sin limites de noches o pax",
    },
    messages: {
      loadError: "No se pudieron cargar los extras.",
      referencesError: "No se pudieron cargar las categorias o tarifas.",
      saveError: "No se pudo guardar el extra.",
      deleteError: "No se pudo eliminar el extra.",
      toggleError: "No se pudo cambiar el estado del extra.",
      saveSuccess: "Extra guardado.",
      deleteSuccess: "Extra eliminado.",
      deactivateSuccess: "Extra desactivado.",
      activateSuccess: "Extra activado.",
      validationName: "El nombre es obligatorio.",
      validationAmount: "El importe es obligatorio.",
      validationAmountNonZero: "El importe no puede ser cero.",
      validationMinorUnits: (currency, minorUnits) =>
        `${currency} admite ${minorUnits === 0 ? "solo unidades enteras" : `hasta ${minorUnits} decimales`}.`,
      confirmDelete: (name) => `Eliminar ${name}?`,
      deleteDialogHelp: "La regla comercial se eliminar\u00e1 de forma permanente. Esta acci\u00f3n no se puede deshacer.",
      otaLinkedTooltip: () => "Vinculado a OTAs.",
      otaNotLinkedTooltip:
        "Aun no esta vinculado a ninguna OTA. Usa el mismo nombre exacto y condiciones de cobro compatibles si quieres mapeo automatico.",
    },
  },
  ca: {
    header: {
      kicker: "Cabina de settings",
      title: "Extres i descomptes",
      subtitle:
        "Crea suplements, descomptes i carrecs obligatoris amb logica de preu, validesa i segmentacio d'inventari.",
    },
    actions: {
      refresh: "Refresca",
      create: "Nou extra",
      edit: "Edita",
      delete: "Elimina",
      deactivate: "Desactiva",
      activate: "Activa",
      cancel: "Cancel.la",
      save: "Desa extra",
      deleting: "Eliminant...",
      close: "Tanca modal",
      viewOnly: "Nomes vista",
    },
    kpis: {
      totalLabel: "Configurats",
      totalFoot: "Total d'extres i descomptes",
      automaticLabel: "Automatics",
      automaticFoot: "S'apliquen sense accio manual",
      mandatoryLabel: "Obligatoris",
      mandatoryFoot: "Requerits al flux de reserva",
      inclusiveLabel: "Inclusius",
      inclusiveFoot: "Ja inclosos a la tarifa",
      validityLabel: "Amb validesa",
      validityFoot: "Lligats a una finestra de dates",
    },
    filters: {
      scopeLabel: "Filtre de scope",
      quickViewLabel: "Vista rapida",
      searchPlaceholder: "Cerca per nom, descripcio, categoria o tarifa...",
      allScopes: "Tots els scopes",
      allRecords: "Tots els registres",
      automaticOnly: "Nomes automatics",
      mandatoryOnly: "Nomes obligatoris",
      inclusiveOnly: "Nomes inclusius",
      datedOnly: "Amb validesa",
    },
    states: {
      loadingTitle: "Carregant extres...",
      loadingText: "Obtenint extres, categories i tarifes.",
      loadErrorTitle: "No s'han pogut carregar els extres",
      loadErrorText: "Prova de refrescar el panell.",
      businessUnitErrorTitle: "Unitat de negoci no disponible",
      businessUnitMissingText: "Selecciona una unitat de negoci activa abans de gestionar extres.",
      businessUnitInvalidText: "El business_unit_id de l'URL no es valid.",
      businessUnitMismatchText:
        "Aquesta pagina pertany a una altra unitat de negoci. Canvia a aquella unitat o torna a obrir Extres des de Settings.",
      emptyTitle: "No hi ha extres",
      emptyText: "Crea el primer extra o relaxa els filtres actius.",
    },
    table: {
      extra: "Extra",
      pricing: "Preu",
      scope: "Scope",
      targeting: "Segmentacio",
      validity: "Validesa",
      flags: "Flags",
      actions: "Accions",
      roomCategories: "Categories",
      roomRates: "Tarifes",
      allInventory: "Tot l'inventari",
      allCategories: "Totes les categories",
      allRates: "Totes les tarifes",
      noDescription: "Sense descripcio",
      noValidity: "Sense finestra de validesa",
      nights: "Nits",
      pax: "Pax",
      automatic: "Automatic",
      mandatory: "Obligatori",
      inclusive: "Inclusiu",
      bookingEngine: "Booking engine",
      none: "Cap",
      from: "Des de",
      until: "Fins a",
    },
    modal: {
      kicker: "Configuracio d'extres",
      createTitle: "Crear extra o descompte",
      editTitle: "Editar extra o descompte",
      subtitle: "Defineix en un sol lloc la regla comercial, l'abast operatiu i la segmentacio.",
      otaHint:
        "Si aquest extra tambe existira en una OTA, usa exactament el mateix nom i les mateixes condicions de cobrament perque el mapatge automatic el reconega.",
      sections: {
        commercialTitle: "Configuracio comercial",
        commercialText: "Nom, model d'import i descripcio curta per a operacions.",
        logicTitle: "Logica de cobrament",
        logicText: "Decideix el scope de la reserva i com es cobra l'import.",
        targetingTitle: "Segmentacio d'inventari",
        targetingText:
          "Inclou o exclou categories i tarifes concretes. Un selector buit no limita aquesta dimensio.",
        restrictionsTitle: "Restriccions",
        restrictionsText: "Limits opcionals d'estada o ocupacio per a la regla.",
        switchesTitle: "Comportament",
        switchesText:
          "Controla la visibilitat al booking engine i si l'extra es inclusiu, automatic o obligatori.",
        summaryTitle: "Resum en viu",
        summaryText: "Vista previa de la regla abans de desar-la.",
        targetsTitle: "Segmentacio seleccionada",
        targetsText: "Si deixes els dos selectors buits, la regla s'aplicara a tot l'inventari.",
      },
      help: {
        reservationScope: "El scope de reserva de grup ignora la unitat de cobrament i els limits de pax.",
        roomScope: "El scope per reserva individual tambe pot cobrar per adult, infant o pax.",
        currency:
          "Els imports fixos poden usar qualsevol moneda habilitada; el servidor els converteix amb la taxa configurada. Els percentatges son independents de la moneda.",
        referencesLoading: "Carregant categories i tarifes...",
        referencesError: "No s'han pogut carregar les categories o tarifes.",
      },
    },
    fields: {
      name: "Nom",
      amount: "Import",
      amountType: "Tipus d'import",
      currency: "Moneda",
      scope: "Scope",
      chargePeriod: "Periode de cobrament",
      chargeUnit: "Unitat de cobrament",
      validFrom: "Valid des de",
      validTo: "Valid fins a",
      roomCategories: "Categories",
      roomRates: "Tarifes",
      minNights: "Nits min",
      maxNights: "Nits max",
      minPax: "Pax min",
      maxPax: "Pax max",
      description: "Descripcio",
      inclusive: "Inclusiu (ja inclos a la tarifa)",
      automatic: "Aplicar automaticament",
      mandatory: "Obligatori",
      bookingEngineVisible: "Visible al booking engine",
      bookingEngineVisibleHint:
        "L'hoste podra seleccionar aquest extra a la reserva directa. Els extres inactius no es mostren.",
      roomCategoryTargetingMode: "Regla de categories",
      roomRateTargetingMode: "Regla de tarifes",
    },
    placeholders: {
      name: "Suplement d'esmorzar",
      description: "Descripcio interna per a l'equip",
      roomCategories: "Selecciona una o diverses categories",
      roomRates: "Selecciona una o diverses tarifes",
      noOptions: "No hi ha opcions coincidents",
      noCode: "sense codi",
    },
    options: {
      scope: {
        per_room: "Per reserva individual",
        per_reservation: "Per reserva de grup",
      },
      chargePeriod: {
        per_stay: "Per estada",
        per_night: "Per nit",
      },
      chargeUnit: {
        per_room: "Per habitacio",
        per_adult: "Per adult",
        per_child: "Per infant",
        per_pax: "Per pax",
      },
      amountType: {
        absolute: "Import fix",
        relative: "Relatiu (%)",
      },
      targetingMode: {
        include: "Nomes les seleccionades",
        exclude: "Totes excepte",
      },
      scopeFilter: {
        all: "Tots els scopes",
        per_room: "Per reserva individual",
        per_reservation: "Per reserva de grup",
      },
      quickView: {
        all: "Tots els registres",
        automatic: "Nomes automatics",
        mandatory: "Nomes obligatoris",
        inclusive: "Nomes inclusius",
        dated: "Amb validesa",
      },
    },
    summary: {
      amount: "Import",
      charge: "Cobrament",
      scope: "Scope",
      validity: "Validesa",
      restrictions: "Restriccions",
      flags: "Flags",
      connection: "Estat OTA",
      linked: "Enllacat a OTAs",
      notLinked: "Encara no enllacat a OTAs",
      categories: "Categories",
      rates: "Tarifes",
      allInventory: "Aplica a totes les categories i tarifes",
      includeOnly: "Nomes seleccionades",
      allExcept: "Totes excepte",
      noFlags: "Sense flags especials",
      noRestrictions: "Sense limits de nits o pax",
    },
    messages: {
      loadError: "No s'han pogut carregar els extres.",
      referencesError: "No s'han pogut carregar les categories o tarifes.",
      saveError: "No s'ha pogut desar l'extra.",
      deleteError: "No s'ha pogut eliminar l'extra.",
      toggleError: "No s'ha pogut canviar l'estat de l'extra.",
      saveSuccess: "Extra desat.",
      deleteSuccess: "Extra eliminat.",
      deactivateSuccess: "Extra desactivat.",
      activateSuccess: "Extra activat.",
      validationName: "El nom es obligatori.",
      validationAmount: "L'import es obligatori.",
      validationAmountNonZero: "L'import no pot ser zero.",
      validationMinorUnits: (currency, minorUnits) =>
        `${currency} admet ${minorUnits === 0 ? "nomes unitats senceres" : `fins a ${minorUnits} decimals`}.`,
      confirmDelete: (name) => `Eliminar ${name}?`,
      deleteDialogHelp: "La regla comercial s'eliminar\u00e0 de manera permanent. Aquesta acci\u00f3 no es pot desfer.",
      otaLinkedTooltip: () => "Enllacat a OTAs.",
      otaNotLinkedTooltip:
        "Encara no esta enllacat a cap OTA. Usa el mateix nom exacte i condicions de cobrament compatibles si vols mapatge automatic.",
    },
  },
};

const DEMO_EXTRAS_MESSAGES = {
  en: {
    readOnly: "Demo · View only. Changes are disabled.",
    detail: "View extra or discount",
    active: "Active",
    inactive: "Inactive",
    update: "This demo JSON does not include the complete extras catalog and its references. Update the demo data and refresh to try again.",
    empty: "No extras match this view.",
    ota: "OTA status · Not included in this demo",
    currency: "The stored currency is shown without conversion.",
    defaults: "Unconfigured values use the PMS defaults: currency EUR, unchecked flags and unrestricted targeting.",
    restrictions: "Stored limits are shown, including limits that do not apply to the selected charge logic.",
  },
  es: {
    readOnly: "Demo · Solo consulta. Los cambios están deshabilitados.",
    detail: "Ver extra o descuento",
    active: "Activo",
    inactive: "Inactivo",
    update: "Este JSON de la demo no incluye el catálogo completo de extras y sus referencias. Actualiza los datos de la demo y pulsa Refrescar para reintentar.",
    empty: "No hay extras que coincidan con esta vista.",
    ota: "Estado OTA · No incluido en esta demo",
    currency: "Se muestra la moneda guardada, sin conversión.",
    defaults: "Los valores sin configurar usan los valores por defecto del PMS: moneda EUR, indicadores desmarcados y segmentación sin restricciones.",
    restrictions: "Se muestran los límites guardados, incluidos los que no se aplican a la lógica de cobro seleccionada.",
  },
  ca: {
    readOnly: "Demo · Només consulta. Els canvis estan deshabilitats.",
    detail: "Veure extra o descompte",
    active: "Actiu",
    inactive: "Inactiu",
    update: "Aquest JSON de la demo no inclou el catàleg complet d'extres i les seves referències. Actualitza les dades de la demo i prem Refrescar per tornar-ho a provar.",
    empty: "No hi ha extres que coincideixin amb aquesta vista.",
    ota: "Estat OTA · No inclòs en aquesta demo",
    currency: "Es mostra la moneda desada, sense conversió.",
    defaults: "Els valors sense configurar utilitzen els valors per defecte del PMS: moneda EUR, indicadors desmarcats i segmentació sense restriccions.",
    restrictions: "Es mostren els límits desats, inclosos els que no s'apliquen a la lògica de cobrament seleccionada.",
  },
};

function createEmptyExtraForm() {
  return {
    name: "",
    description: "",
    currency: "EUR",
    charge_period: "per_stay",
    charge_unit: "per_room",
    scope: "per_room",
    min_nights: "",
    max_nights: "",
    min_pax: "",
    max_pax: "",
    amount: "",
    amount_type: "absolute",
    inclusive: false,
    apply_automatically: false,
    is_mandatory: false,
    booking_engine_visible: false,
    room_category_ids: [],
    room_category_targeting_mode: "include",
    room_rate_ids: [],
    room_rate_targeting_mode: "include",
    valid_from: "",
    valid_to: "",
  };
}

function normalizeIdArray(values) {
  if (!Array.isArray(values)) return [];
  return [
    ...new Set(
      values
        .map((value) => Number(value))
        .filter((value) => Number.isInteger(value) && value > 0)
    ),
  ];
}

function normalizeTargetingMode(value) {
  return value === "exclude" ? "exclude" : "include";
}

function normalizeIsoDate(value) {
  return value ? String(value).slice(0, 10) : "";
}

function normalizeInputNumber(value) {
  if (value == null || value === "") return "";
  return String(value);
}

function normalizeExtraForForm(extra) {
  return {
    ...createEmptyExtraForm(),
    name: String(extra?.name || ""),
    description: String(extra?.description || ""),
    currency: String(extra?.currency || "EUR"),
    charge_period: String(extra?.charge_period || "per_stay"),
    charge_unit: String(extra?.charge_unit || "per_room"),
    scope: String(extra?.scope || "per_room"),
    min_nights: normalizeInputNumber(extra?.min_nights),
    max_nights: normalizeInputNumber(extra?.max_nights),
    min_pax: normalizeInputNumber(extra?.min_pax),
    max_pax: normalizeInputNumber(extra?.max_pax),
    amount: normalizeInputNumber(extra?.amount),
    amount_type: String(extra?.amount_type || "absolute"),
    inclusive: Boolean(extra?.inclusive),
    apply_automatically: Boolean(extra?.apply_automatically),
    is_mandatory: Boolean(extra?.is_mandatory),
    booking_engine_visible: Boolean(extra?.booking_engine_visible),
    room_category_ids: normalizeIdArray(extra?.room_category_ids),
    room_category_targeting_mode: normalizeTargetingMode(
      extra?.room_category_targeting_mode
    ),
    room_rate_ids: normalizeIdArray(extra?.room_rate_ids),
    room_rate_targeting_mode: normalizeTargetingMode(
      extra?.room_rate_targeting_mode
    ),
    valid_from: normalizeIsoDate(extra?.valid_from),
    valid_to: normalizeIsoDate(extra?.valid_to),
  };
}

function parseNullableNumber(value, { integer = false } = {}) {
  const text = String(value ?? "").trim();
  if (!text) return null;
  const numericValue = Number(text);
  if (!Number.isFinite(numericValue)) return null;
  return integer ? Math.trunc(numericValue) : numericValue;
}

export function getSettingsExtraCurrencyPrecisionError(formData, currencyCatalog = []) {
  if (formData?.amount_type !== "absolute") return null;
  const currency = String(formData?.currency || "").trim().toUpperCase();
  if (hasValidCurrencyPrecision(formData?.amount, currency, currencyCatalog)) return null;
  return { currency, minorUnits: getCurrencyMinorUnits(currency, currencyCatalog) };
}

function formatAmountValue(amount, amountType, currency, locale, currencyCatalog = []) {
  const numericValue = Number(amount);
  if (!Number.isFinite(numericValue)) return "-";

  if (amountType === "relative") {
    const isInteger = Number.isInteger(numericValue);
    return `${numericValue.toLocaleString(locale, {
      minimumFractionDigits: isInteger ? 0 : 2,
      maximumFractionDigits: 2,
    })}%`;
  }

  return formatCurrencyAmount(numericValue, currency, { locale, catalog: currencyCatalog });
}

function formatDateValue(value, formatter) {
  if (!value) return "";
  const date = new Date(`${value}T00:00:00`);
  if (Number.isNaN(date.getTime())) return value;
  return formatter.format(date);
}

function formatValidityRange(validFrom, validTo, formatter, copy) {
  if (!validFrom && !validTo) return copy.table.noValidity;
  if (validFrom && validTo) {
    return `${formatDateValue(validFrom, formatter)} - ${formatDateValue(validTo, formatter)}`;
  }
  if (validFrom) return `${copy.table.from} ${formatDateValue(validFrom, formatter)}`;
  return `${copy.table.until} ${formatDateValue(validTo, formatter)}`;
}

function getSelectStyles(isLightTheme) {
  const palette = isLightTheme
    ? {
        surface: "#ffffff",
        soft: "#f4f8fb",
        text: "#26354f",
        muted: "#5b6680",
        border: "#7c90a7",
        borderSoft: "#d7e2ec",
        selected: "#223a7a",
        selectedText: "#ffffff",
        focus: "#3db6e9",
        optionFocus: "#eaf7fc",
        chip: "#edf8fc",
        chipText: "#223a7a",
        chipBorder: "#9bcfe4",
      }
    : {
        surface: "#0e1730",
        soft: "#152344",
        text: "#dfe8f7",
        muted: "#aebbd0",
        border: "rgba(61, 182, 233, 0.62)",
        borderSoft: "rgba(99, 193, 199, 0.24)",
        selected: "#223a7a",
        selectedText: "#ffffff",
        focus: "#3db6e9",
        optionFocus: "#172a4d",
        chip: "rgba(61, 182, 233, 0.14)",
        chipText: "#a8e2f7",
        chipBorder: "rgba(61, 182, 233, 0.5)",
      };

  return {
    control: (base, state) => ({
      ...base,
      minHeight: 38,
      borderRadius: 9,
      borderColor: state.isFocused ? palette.focus : palette.border,
      background: palette.surface,
      boxShadow: state.isFocused ? "0 0 0 3px rgba(61, 182, 233, 0.24)" : "none",
      fontSize: "0.75rem",
      "&:hover": {
        borderColor: palette.focus,
      },
    }),
    valueContainer: (base) => ({
      ...base,
      padding: "3px 8px",
      gap: 4,
    }),
    placeholder: (base) => ({
      ...base,
      color: palette.muted,
    }),
    input: (base) => ({
      ...base,
      color: palette.text,
    }),
    singleValue: (base) => ({
      ...base,
      color: palette.text,
    }),
    multiValue: (base) => ({
      ...base,
      borderRadius: 999,
      background: palette.chip,
      border: `1px solid ${palette.chipBorder}`,
    }),
    multiValueLabel: (base) => ({
      ...base,
      color: palette.chipText,
      fontWeight: 700,
    }),
    multiValueRemove: (base) => ({
      ...base,
      borderRadius: 999,
      color: palette.chipText,
      ":hover": {
        background: "#fdeff1",
        color: "#9b3445",
      },
    }),
    menu: (base) => ({
      ...base,
      borderRadius: 10,
      overflow: "hidden",
      border: `1px solid ${palette.borderSoft}`,
      background: palette.surface,
      boxShadow: isLightTheme
        ? "0 18px 38px rgba(34, 58, 122, 0.16)"
        : "0 20px 42px rgba(2, 8, 28, 0.5)",
      zIndex: 27000,
    }),
    menuPortal: (base) => ({
      ...base,
      zIndex: 27000,
    }),
    option: (base, state) => ({
      ...base,
      cursor: "pointer",
      background: state.isSelected ? palette.selected : state.isFocused ? palette.optionFocus : palette.surface,
      color: state.isSelected ? palette.selectedText : palette.text,
      fontSize: "0.75rem",
    }),
    dropdownIndicator: (base) => ({
      ...base,
      color: isLightTheme ? palette.selected : palette.focus,
    }),
    indicatorSeparator: (base) => ({
      ...base,
      backgroundColor: palette.borderSoft,
    }),
  };
}


function buildRateOptionLabel(rate, copy) {
  return `${rate?.name || "-"} (${rate?.rate_code || copy.placeholders.noCode})`;
}

const SettingsExtras = () => {
  const demoModeActive = isDemoMode();
  const { themeId, languageId, language } = usePmsTheme();
  const { baseCurrency, currencies, currencyCatalog = [] } = useCurrencyDisplay();
  const isLightTheme = themeId === "light";
  const extrasCopy = SETTINGS_EXTRAS_MESSAGES[languageId] || SETTINGS_EXTRAS_MESSAGES.en;
  const demoCopy = DEMO_EXTRAS_MESSAGES[languageId] || DEMO_EXTRAS_MESSAGES.en;
  const locale = languageId === "en" ? "en-GB" : language?.locale || "en-GB";
  const [searchParams] = useSearchParams();
  const queryBusinessUnitId = searchParams.get("business_unit_id");

  const [sessionUser, setSessionUser] = useState(() => demoModeActive ? readDemoUser() : readStoredUser());
  const currentUser = (demoModeActive ? readDemoUser() : sessionUser) || {};
  const businessUnitScope = useMemo(
    () =>
      demoModeActive ? { businessUnitId: DEMO_BUSINESS_UNIT_ID, errorCode: null } : resolveSettingsExtrasBusinessUnit({
        queryBusinessUnitId,
        activeBusinessUnitId:
          currentUser?.active_business_unit_id ?? currentUser?.business_unit_id,
      }),
    [
      demoModeActive,
      currentUser?.active_business_unit_id,
      currentUser?.business_unit_id,
      queryBusinessUnitId,
    ]
  );
  const businessUnitId = businessUnitScope.businessUnitId;
  const businessUnitError = useMemo(() => {
    if (businessUnitScope.errorCode === SETTINGS_EXTRAS_BUSINESS_UNIT_ERRORS.INVALID_QUERY) {
      return extrasCopy.states.businessUnitInvalidText;
    }
    if (businessUnitScope.errorCode === SETTINGS_EXTRAS_BUSINESS_UNIT_ERRORS.QUERY_MISMATCH) {
      return extrasCopy.states.businessUnitMismatchText;
    }
    if (businessUnitScope.errorCode === SETTINGS_EXTRAS_BUSINESS_UNIT_ERRORS.MISSING_ACTIVE) {
      return extrasCopy.states.businessUnitMissingText;
    }
    return "";
  }, [businessUnitScope.errorCode, extrasCopy.states]);
  const permissions = currentUser?.permissions || {};
  const isAdminRole = Number(currentUser?.id) === 1;
  const canManage = !demoModeActive && (isAdminRole || Boolean(permissions["hotel_settings.manage_extras_actions"]));

  const [extras, setExtras] = useState([]);
  const [roomCategories, setRoomCategories] = useState([]);
  const [roomRates, setRoomRates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [referencesError, setReferencesError] = useState("");
  const [showExtraModal, setShowExtraModal] = useState(false);
  const [editingExtra, setEditingExtra] = useState(null);
  const [pendingDeleteExtra, setPendingDeleteExtra] = useState(null);
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [togglingId, setTogglingId] = useState(null);
  const [formData, setFormData] = useState(() => ({
    ...createEmptyExtraForm(),
    currency: baseCurrency || "EUR",
  }));
  const [searchText, setSearchText] = useState("");
  const [scopeFilter, setScopeFilter] = useState("all");
  const [quickViewFilter, setQuickViewFilter] = useState("all");
  const [toast, setToast] = useState(null);
  const toastTimerRef = useRef(null);
  const extraModalRef = useRef(null);
  const deleteDialogRef = useRef(null);
  const savingRef = useRef(saving);
  const deletingIdRef = useRef(deletingId);
  const businessUnitIdRef = useRef(businessUnitId);
  businessUnitIdRef.current = businessUnitId;
  savingRef.current = saving;
  deletingIdRef.current = deletingId;

  const dateFormatter = useMemo(
    () =>
      new Intl.DateTimeFormat(locale, {
        day: "2-digit",
        month: "short",
        year: "numeric",
      }),
    [locale]
  );

  const selectStyles = useMemo(() => getSelectStyles(isLightTheme), [isLightTheme]);

  useEffect(() => {
    if (demoModeActive) return undefined;
    const syncSessionFromStorage = () => {
      if (isDemoMode()) return;
      setSessionUser(readStoredUser());
    };

    window.addEventListener("palantir-session-updated", syncSessionFromStorage);
    return () => {
      window.removeEventListener("palantir-session-updated", syncSessionFromStorage);
    };
  }, [demoModeActive]);

  const showToast = useCallback((message, tone = "success") => {
    if (!message) return;

    if (toastTimerRef.current) {
      window.clearTimeout(toastTimerRef.current);
    }

    setToast({ message, tone });
    toastTimerRef.current = window.setTimeout(() => {
      setToast(null);
      toastTimerRef.current = null;
    }, 3200);
  }, []);

  useEffect(() => {
    return () => {
      if (toastTimerRef.current) {
        window.clearTimeout(toastTimerRef.current);
      }
    };
  }, []);

  const fetchExtras = useCallback(async () => {
    if (demoModeActive || isDemoMode()) return false;
    if (!businessUnitId) return false;
    const requestedBusinessUnitId = businessUnitId;

    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/extras?business_unit_id=${requestedBusinessUnitId}`
      );
      if (businessUnitIdRef.current !== requestedBusinessUnitId) return false;
      setExtras(Array.isArray(response.data) ? response.data : []);
      setLoadError("");
      return true;
    } catch (error) {
      if (businessUnitIdRef.current !== requestedBusinessUnitId) return false;
      console.error("[SettingsExtras][GET][ERROR]", error);
      setLoadError(extrasCopy.messages.loadError);
      return false;
    }
  }, [businessUnitId, demoModeActive, extrasCopy.messages.loadError]);

  const fetchReferences = useCallback(
    async ({ notify = false } = {}) => {
      if (demoModeActive || isDemoMode()) return false;
      if (!businessUnitId) return false;
      const requestedBusinessUnitId = businessUnitId;

      try {
        const [categoriesResponse, ratesResponse] = await Promise.all([
          axios.get(
            `${process.env.REACT_APP_API_URL}/api/room_categories?business_unit_id=${requestedBusinessUnitId}`
          ),
          axios.get(
            `${process.env.REACT_APP_API_URL}/api/roomRates/list?business_unit_id=${requestedBusinessUnitId}`
          ),
        ]);

        if (businessUnitIdRef.current !== requestedBusinessUnitId) return false;
        setRoomCategories(Array.isArray(categoriesResponse.data) ? categoriesResponse.data : []);
        setRoomRates(Array.isArray(ratesResponse.data) ? ratesResponse.data : []);
        setReferencesError("");
        return true;
      } catch (error) {
        if (businessUnitIdRef.current !== requestedBusinessUnitId) return false;
        console.error("[SettingsExtras][REFERENCES][ERROR]", error);
        setReferencesError(extrasCopy.messages.referencesError);
        if (notify) {
          showToast(extrasCopy.messages.referencesError, "error");
        }
        return false;
      }
    },
    [businessUnitId, demoModeActive, extrasCopy.messages.referencesError, showToast]
  );

  const refreshAllData = useCallback(
    async ({ notifyReferenceErrors = false } = {}) => {
      if (!businessUnitId) {
        setExtras([]);
        setRoomCategories([]);
        setRoomRates([]);
        setLoadError("");
        setReferencesError("");
        setLoading(false);
        return false;
      }

      const requestedBusinessUnitId = businessUnitId;
      setLoading(true);
      if (demoModeActive) {
        setLoadError("");
        setReferencesError("");
        setShowExtraModal(false);
        setEditingExtra(null);
        try {
          const { data } = await axios.get("/api/demo/extras-settings", {
            params: { business_unit_id: DEMO_BUSINESS_UNIT_ID },
          });
          if (!data || ![data.extras, data.roomCategories, data.roomRates].every(Array.isArray)) {
            throw new Error("Incomplete demo extras catalog");
          }
          const categories = new Set(data.roomCategories.map((category) => category.id));
          const rates = new Set(data.roomRates.map((rate) => rate.id));
          const hasWrongScope = [data.extras, data.roomCategories, data.roomRates].some((rows) =>
            rows.some((row) => !row || row.business_unit_id !== DEMO_BUSINESS_UNIT_ID)
          );
          if (hasWrongScope || data.extras.some((extra) =>
            (extra.room_category_ids !== null && !Array.isArray(extra.room_category_ids)) ||
            (extra.room_rate_ids !== null && !Array.isArray(extra.room_rate_ids)) ||
            (extra.room_category_ids || []).some((id) => !categories.has(id)) ||
            (extra.room_rate_ids || []).some((id) => !rates.has(id))
          )) throw new Error("Invalid demo extras references");
          if (businessUnitIdRef.current !== requestedBusinessUnitId) return false;
          setExtras(data.extras);
          setRoomCategories(data.roomCategories);
          setRoomRates(data.roomRates);
          return true;
        } catch (_error) {
          if (businessUnitIdRef.current !== requestedBusinessUnitId) return false;
          setExtras([]);
          setRoomCategories([]);
          setRoomRates([]);
          setLoadError(demoCopy.update);
          return false;
        } finally {
          if (businessUnitIdRef.current === requestedBusinessUnitId) setLoading(false);
        }
      }
      await Promise.all([fetchExtras(), fetchReferences({ notify: notifyReferenceErrors })]);
      if (businessUnitIdRef.current === requestedBusinessUnitId) {
        setLoading(false);
      }
      return true;
    },
    [businessUnitId, demoModeActive, demoCopy.update, fetchExtras, fetchReferences]
  );

  useEffect(() => {
    void refreshAllData();
  }, [refreshAllData]);

  useEffect(() => {
    if (demoModeActive) return;
    if (formData.charge_period === "per_stay") {
      setFormData((previous) => ({
        ...previous,
        min_nights: "",
        max_nights: "",
      }));
    }
  }, [demoModeActive, formData.charge_period]);

  useEffect(() => {
    if (demoModeActive) return;
    if (formData.scope === "per_reservation" || formData.charge_unit === "per_room") {
      setFormData((previous) => ({
        ...previous,
        min_pax: "",
        max_pax: "",
      }));
    }
  }, [demoModeActive, formData.charge_unit, formData.scope]);

  const roomCategoryById = useMemo(() => {
    const nextMap = new Map();
    roomCategories.forEach((category) => {
      nextMap.set(Number(category.id), category);
    });
    return nextMap;
  }, [roomCategories]);

  const roomRateById = useMemo(() => {
    const nextMap = new Map();
    roomRates.forEach((rate) => {
      nextMap.set(Number(rate.id), rate);
    });
    return nextMap;
  }, [roomRates]);

  const roomCategoryOptions = useMemo(
    () =>
      roomCategories.map((category) => ({
        value: Number(category.id),
        label: category.name,
      })),
    [roomCategories]
  );

  const roomRateOptions = useMemo(
    () =>
      roomRates.map((rate) => ({
        value: Number(rate.id),
        label: buildRateOptionLabel(rate, extrasCopy),
      })),
    [extrasCopy, roomRates]
  );

  const selectedCategoryOptions = useMemo(
    () =>
      normalizeIdArray(formData.room_category_ids)
        .map((id) => {
          const category = roomCategoryById.get(Number(id));
          return category ? { value: Number(category.id), label: category.name } : null;
        })
        .filter(Boolean),
    [formData.room_category_ids, roomCategoryById]
  );

  const selectedRateOptions = useMemo(
    () =>
      normalizeIdArray(formData.room_rate_ids)
        .map((id) => {
          const rate = roomRateById.get(Number(id));
          return rate
            ? {
                value: Number(rate.id),
                label: buildRateOptionLabel(rate, extrasCopy),
              }
            : null;
        })
        .filter(Boolean),
    [extrasCopy, formData.room_rate_ids, roomRateById]
  );

  const stats = useMemo(() => {
    const automatic = extras.filter((extra) => Boolean(extra.apply_automatically)).length;
    const mandatory = extras.filter((extra) => Boolean(extra.is_mandatory)).length;
    const inclusive = extras.filter((extra) => Boolean(extra.inclusive)).length;
    const dated = extras.filter((extra) => extra.valid_from || extra.valid_to).length;

    return {
      total: extras.length,
      automatic,
      mandatory,
      inclusive,
      dated,
    };
  }, [extras]);

  const filteredExtras = useMemo(() => {
    const needle = searchText.trim().toLowerCase();

    return [...extras]
      .filter((extra) => {
        if (scopeFilter !== "all" && String(extra.scope || "") !== scopeFilter) {
          return false;
        }

        if (quickViewFilter === "automatic" && !extra.apply_automatically) return false;
        if (quickViewFilter === "mandatory" && !extra.is_mandatory) return false;
        if (quickViewFilter === "inclusive" && !extra.inclusive) return false;
        if (quickViewFilter === "dated" && !extra.valid_from && !extra.valid_to) return false;

        if (!needle) return true;

        const haystack = [
          extra?.name,
          extra?.description,
          ...(Array.isArray(extra?.room_category_names) ? extra.room_category_names : []),
          ...(Array.isArray(extra?.room_rate_names) ? extra.room_rate_names : []),
          ...(demoModeActive ? normalizeIdArray(extra.room_category_ids).map((id) => roomCategoryById.get(id)?.name) : []),
          ...(demoModeActive ? normalizeIdArray(extra.room_rate_ids).map((id) => roomRateById.get(id)?.name) : []),
        ]
          .filter(Boolean)
          .join(" ")
          .toLowerCase();

        return haystack.includes(needle);
      })
      .sort((left, right) => String(left?.name || "").localeCompare(String(right?.name || ""), locale));
  }, [demoModeActive, extras, locale, quickViewFilter, roomCategoryById, roomRateById, scopeFilter, searchText]);

  const openCreateModal = () => {
    if (demoModeActive || isDemoMode()) return;
    if (!businessUnitId) {
      showToast(businessUnitError || extrasCopy.states.businessUnitMissingText, "error");
      return;
    }
    setEditingExtra(null);
    setFormData({ ...createEmptyExtraForm(), currency: baseCurrency || "EUR" });
    setShowExtraModal(true);
  };

  const openEditModal = (extra, { viewOnly = false } = {}) => {
    if ((demoModeActive || isDemoMode()) && (!viewOnly || loading || loadError)) return;
    if (!businessUnitId) {
      showToast(businessUnitError || extrasCopy.states.businessUnitMissingText, "error");
      return;
    }
    setEditingExtra(extra);
    setFormData(normalizeExtraForForm(extra));
    setShowExtraModal(true);
  };

  const closeModal = () => {
    if (saving) return;
    setShowExtraModal(false);
    setEditingExtra(null);
    setFormData({ ...createEmptyExtraForm(), currency: baseCurrency || "EUR" });
  };

  useEffect(() => {
    if (!showExtraModal) return undefined;

    const previousFocus = document.activeElement;
    const dialog = extraModalRef.current;
    const focusableSelector =
      'button:not(:disabled), input:not(:disabled), select:not(:disabled), textarea:not(:disabled), [tabindex]:not([tabindex="-1"]):not(:disabled)';
    const initialFocus = dialog?.querySelector('input[name="name"]:not(:disabled)') || dialog?.querySelector(focusableSelector);
    if (initialFocus instanceof HTMLElement) initialFocus.focus();
    else dialog?.focus();

    const handleDialogKeyDown = (event) => {
      if (event.key === "Escape" && !savingRef.current) {
        event.preventDefault();
        closeModal();
        return;
      }
      if (event.key !== "Tab" || !dialog) return;
      const focusable = Array.from(dialog.querySelectorAll(focusableSelector));
      if (focusable.length === 0) {
        event.preventDefault();
        dialog.focus();
        return;
      }
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    document.addEventListener("keydown", handleDialogKeyDown);
    return () => {
      document.removeEventListener("keydown", handleDialogKeyDown);
      if (previousFocus instanceof HTMLElement) previousFocus.focus();
    };
  }, [showExtraModal]);

  useEffect(() => {
    if (!pendingDeleteExtra) return undefined;

    const previousFocus = document.activeElement;
    const dialog = deleteDialogRef.current;
    const focusableSelector =
      'button:not(:disabled), [tabindex]:not([tabindex="-1"]):not(:disabled)';
    const initialFocus = dialog?.querySelector(focusableSelector);
    if (initialFocus instanceof HTMLElement) initialFocus.focus();
    else dialog?.focus();

    const handleDeleteDialogKeyDown = (event) => {
      if (event.key === "Escape" && !deletingIdRef.current) {
        event.preventDefault();
        setPendingDeleteExtra(null);
        return;
      }
      if (event.key !== "Tab" || !dialog) return;
      const focusable = Array.from(dialog.querySelectorAll(focusableSelector));
      if (focusable.length === 0) {
        event.preventDefault();
        dialog.focus();
        return;
      }
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    document.addEventListener("keydown", handleDeleteDialogKeyDown);
    return () => {
      document.removeEventListener("keydown", handleDeleteDialogKeyDown);
      if (previousFocus instanceof HTMLElement) previousFocus.focus();
    };
  }, [pendingDeleteExtra]);

  const handleFormChange = (field, value) => {
    if (demoModeActive || isDemoMode()) return;
    setFormData((previous) => ({
      ...previous,
      [field]: value,
    }));
  };

  const handleCategoryChange = (selected) => {
    if (demoModeActive || isDemoMode()) return;
    const nextCategoryIds = Array.isArray(selected) ? selected.map((option) => Number(option.value)) : [];
    setFormData((previous) => ({
      ...previous,
      room_category_ids: normalizeIdArray(nextCategoryIds),
    }));
  };

  const handleRateChange = (selected) => {
    if (demoModeActive || isDemoMode()) return;
    const nextRateIds = Array.isArray(selected) ? selected.map((option) => Number(option.value)) : [];
    setFormData((previous) => ({
      ...previous,
      room_rate_ids: normalizeIdArray(nextRateIds),
    }));
  };

  const handleSubmit = async () => {
    if (demoModeActive || isDemoMode()) return;
    if (!businessUnitId) {
      showToast(businessUnitError || extrasCopy.states.businessUnitMissingText, "error");
      return;
    }

    const payload = {
      business_unit_id: businessUnitId,
      name: String(formData.name || "").trim(),
      description: String(formData.description || "").trim(),
      currency: String(formData.currency || baseCurrency || "EUR").toUpperCase(),
      charge_period: formData.charge_period,
      charge_unit: formData.charge_unit,
      scope: formData.scope,
      amount: parseNullableNumber(formData.amount),
      amount_type: formData.amount_type,
      min_nights: parseNullableNumber(formData.min_nights, { integer: true }),
      max_nights: parseNullableNumber(formData.max_nights, { integer: true }),
      min_pax: parseNullableNumber(formData.min_pax, { integer: true }),
      max_pax: parseNullableNumber(formData.max_pax, { integer: true }),
      inclusive: Boolean(formData.inclusive),
      apply_automatically: Boolean(formData.apply_automatically),
      is_mandatory: Boolean(formData.is_mandatory),
      booking_engine_visible: Boolean(formData.booking_engine_visible),
      room_category_ids: normalizeIdArray(formData.room_category_ids),
      room_category_targeting_mode: normalizeTargetingMode(
        formData.room_category_targeting_mode
      ),
      room_rate_ids: normalizeIdArray(formData.room_rate_ids),
      room_rate_targeting_mode: normalizeTargetingMode(
        formData.room_rate_targeting_mode
      ),
      valid_from: formData.valid_from || null,
      valid_to: formData.valid_to || null,
    };

    if (!payload.name) {
      showToast(extrasCopy.messages.validationName, "error");
      return;
    }

    const amountValidationCode = getSettingsExtraAmountValidation(formData.amount);
    if (amountValidationCode === SETTINGS_EXTRA_AMOUNT_VALIDATION.REQUIRED) {
      showToast(extrasCopy.messages.validationAmount, "error");
      return;
    }
    if (amountValidationCode === SETTINGS_EXTRA_AMOUNT_VALIDATION.NON_ZERO) {
      showToast(extrasCopy.messages.validationAmountNonZero, "error");
      return;
    }
    const precisionError = getSettingsExtraCurrencyPrecisionError(
      { ...formData, currency: payload.currency },
      currencyCatalog
    );
    const monetaryFieldsChanged = !editingExtra?.id ||
      Number(formData.amount) !== Number(editingExtra.amount) ||
      String(payload.currency) !== String(editingExtra.currency || "").toUpperCase() ||
      String(formData.amount_type) !== String(editingExtra.amount_type || "absolute");
    if (precisionError && monetaryFieldsChanged) {
      showToast(
        extrasCopy.messages.validationMinorUnits(
          precisionError.currency,
          precisionError.minorUnits
        ),
        "error"
      );
      return;
    }
    setSaving(true);

    try {
      if (editingExtra?.id) {
        await axios.put(`${process.env.REACT_APP_API_URL}/api/extras/${editingExtra.id}`, payload);
      } else {
        await axios.post(`${process.env.REACT_APP_API_URL}/api/extras`, payload);
      }

      await fetchExtras();
      setShowExtraModal(false);
      setEditingExtra(null);
      setFormData({ ...createEmptyExtraForm(), currency: baseCurrency || "EUR" });
      showToast(extrasCopy.messages.saveSuccess, "success");
    } catch (error) {
      console.error("[SettingsExtras][SAVE][ERROR]", error);
      showToast(extrasCopy.messages.saveError, "error");
    } finally {
      setSaving(false);
    }
  };

  const handleToggleActive = async (extra) => {
    if (demoModeActive || isDemoMode()) return;
    if (!extra?.id) return;
    if (!businessUnitId) {
      showToast(businessUnitError || extrasCopy.states.businessUnitMissingText, "error");
      return;
    }
    setTogglingId(extra.id);
    try {
      const { data } = await axios.patch(`${process.env.REACT_APP_API_URL}/api/extras/${extra.id}/toggle-active`);
      setExtras((previous) =>
        previous.map((item) => (item.id === extra.id ? { ...item, is_active: data.is_active } : item))
      );
      showToast(
        data.is_active ? extrasCopy.messages.activateSuccess : extrasCopy.messages.deactivateSuccess,
        "success"
      );
    } catch (error) {
      console.error("[SettingsExtras][TOGGLE][ERROR]", error);
      showToast(extrasCopy.messages.toggleError, "error");
    } finally {
      setTogglingId(null);
    }
  };

  const handleDelete = async (extra) => {
    if (demoModeActive || isDemoMode()) return;
    if (!extra?.id) return;
    if (deletingIdRef.current) return;
    if (!businessUnitId) {
      showToast(businessUnitError || extrasCopy.states.businessUnitMissingText, "error");
      return;
    }

    deletingIdRef.current = extra.id;
    setDeletingId(extra.id);

    try {
      await axios.delete(`${process.env.REACT_APP_API_URL}/api/extras/${extra.id}`);
      setExtras((previous) => previous.filter((item) => item.id !== extra.id));
      setPendingDeleteExtra(null);
      showToast(extrasCopy.messages.deleteSuccess, "success");
    } catch (error) {
      console.error("[SettingsExtras][DELETE][ERROR]", error);
      showToast(extrasCopy.messages.deleteError, "error");
    } finally {
      deletingIdRef.current = null;
      setDeletingId(null);
    }
  };

  const formatChargeLine = useCallback(
    (extra) => {
      const periodLabel =
        extrasCopy.options.chargePeriod[String(extra?.charge_period || "per_stay")] ||
        String(extra?.charge_period || "");
      const unitLabel =
        extrasCopy.options.chargeUnit[String(extra?.charge_unit || "per_room")] ||
        String(extra?.charge_unit || "");

      return extra?.scope === "per_reservation" ? periodLabel : `${periodLabel} / ${unitLabel}`;
    },
    [extrasCopy.options.chargePeriod, extrasCopy.options.chargeUnit]
  );

  const formatRestrictions = useCallback((extra) => {
    if (demoModeActive) {
      const displayLimit = (value) => value == null || value === "" ? "—" : value;
      return {
        nights: `${displayLimit(extra?.min_nights)} / ${displayLimit(extra?.max_nights)}`,
        pax: `${displayLimit(extra?.min_pax)} / ${displayLimit(extra?.max_pax)}`,
      };
    }
    const nightsMin = extra?.charge_period === "per_stay" ? "-" : extra?.min_nights ?? "-";
    const nightsMax = extra?.charge_period === "per_stay" ? "-" : extra?.max_nights ?? "-";
    const paxMin =
      extra?.scope === "per_reservation" || extra?.charge_unit === "per_room" ? "-" : extra?.min_pax ?? "-";
    const paxMax =
      extra?.scope === "per_reservation" || extra?.charge_unit === "per_room" ? "-" : extra?.max_pax ?? "-";

    return {
      nights: `${nightsMin} / ${nightsMax}`,
      pax: `${paxMin} / ${paxMax}`,
    };
  }, [demoModeActive]);

  const getOtaLinkMeta = useCallback(
    (extra) => {
      if (demoModeActive) return { linked: null, label: demoCopy.ota, tooltip: demoCopy.ota };
      const linkedRaw = typeof extra?.ota_linked === "boolean" ? extra.ota_linked : null;
      const linksCountRaw = Number(extra?.ota_links_count ?? 0);
      const legacyProviderLinked = Boolean(extra?.channex_linked) || Boolean(extra?.lighthouse_linked);
      const linked = linkedRaw == null ? legacyProviderLinked || linksCountRaw > 0 : Boolean(linkedRaw);

      return {
        linked,
        label: linked ? extrasCopy.summary.linked : extrasCopy.summary.notLinked,
        tooltip: linked ? extrasCopy.messages.otaLinkedTooltip() : extrasCopy.messages.otaNotLinkedTooltip,
      };
    },
    [
      demoModeActive,
      demoCopy.ota,
      extrasCopy.messages,
      extrasCopy.summary.linked,
      extrasCopy.summary.notLinked,
    ]
  );

  const selectedCategoryNames = useMemo(
    () =>
      normalizeIdArray(formData.room_category_ids)
        .map((id) => roomCategoryById.get(Number(id))?.name)
        .filter(Boolean),
    [formData.room_category_ids, roomCategoryById]
  );

  const selectedRateNames = useMemo(
    () =>
      normalizeIdArray(formData.room_rate_ids)
        .map((id) => {
          const rate = roomRateById.get(Number(id));
          return rate ? buildRateOptionLabel(rate, extrasCopy) : "";
        })
        .filter(Boolean),
    [extrasCopy, formData.room_rate_ids, roomRateById]
  );

  const summaryFlags = useMemo(() => {
    const flags = [];
    if (formData.booking_engine_visible) {
      flags.push({ label: extrasCopy.table.bookingEngine, tone: "brand" });
    }
    if (formData.inclusive) flags.push({ label: extrasCopy.table.inclusive, tone: "connect" });
    if (formData.apply_automatically) flags.push({ label: extrasCopy.table.automatic, tone: "horizon" });
    if (formData.is_mandatory) flags.push({ label: extrasCopy.table.mandatory, tone: "mandatory" });
    return flags;
  }, [
    extrasCopy.table.automatic,
    extrasCopy.table.bookingEngine,
    extrasCopy.table.inclusive,
    extrasCopy.table.mandatory,
    formData.apply_automatically,
    formData.booking_engine_visible,
    formData.inclusive,
    formData.is_mandatory,
  ]);

  const summaryRestrictions = useMemo(() => {
    if (demoModeActive) {
      const restrictions = formatRestrictions({
        min_nights: formData.min_nights, max_nights: formData.max_nights,
        min_pax: formData.min_pax, max_pax: formData.max_pax,
      });
      return `${extrasCopy.table.nights}: ${restrictions.nights} | ${extrasCopy.table.pax}: ${restrictions.pax}`;
    }
    const nightsMin = formData.charge_period === "per_stay" ? "-" : formData.min_nights || "-";
    const nightsMax = formData.charge_period === "per_stay" ? "-" : formData.max_nights || "-";
    const paxMin =
      formData.scope === "per_reservation" || formData.charge_unit === "per_room" ? "-" : formData.min_pax || "-";
    const paxMax =
      formData.scope === "per_reservation" || formData.charge_unit === "per_room" ? "-" : formData.max_pax || "-";

    if (nightsMin === "-" && nightsMax === "-" && paxMin === "-" && paxMax === "-") {
      return extrasCopy.summary.noRestrictions;
    }

    return `${extrasCopy.table.nights}: ${nightsMin} / ${nightsMax} | ${extrasCopy.table.pax}: ${paxMin} / ${paxMax}`;
  }, [
    demoModeActive,
    formatRestrictions,
    extrasCopy.summary.noRestrictions,
    extrasCopy.table.nights,
    extrasCopy.table.pax,
    formData.charge_period,
    formData.charge_unit,
    formData.max_nights,
    formData.max_pax,
    formData.min_nights,
    formData.min_pax,
    formData.scope,
  ]);

  const editingExtraLinkMeta = useMemo(() => getOtaLinkMeta(editingExtra), [editingExtra, getOtaLinkMeta]);

  const menuPortalTarget = typeof document !== "undefined" ? document.body : null;
  const ModalBody = demoModeActive ? "fieldset" : "div";
  const demoDataUnavailable = demoModeActive && (loading || Boolean(loadError));

  return (
    <div className={`sxp-shell${isLightTheme ? " sxp-shell--light" : ""}`}>
      <section className="sxp-stage">
        <div className="sxp-head">
          <div>
            <p className="sxp-kicker">{extrasCopy.header.kicker}</p>
            <h2 className="sxp-title">{extrasCopy.header.title}</h2>
            <p className="sxp-subtitle">{demoModeActive ? demoCopy.readOnly : extrasCopy.header.subtitle}</p>
          </div>

          <div className="sxp-headActions">
            <button
              type="button"
              className="sxp-btn sxp-btn--ghost"
              onClick={() => {
                void refreshAllData({ notifyReferenceErrors: true });
              }}
              disabled={loading || !businessUnitId}
            >
              {extrasCopy.actions.refresh}
            </button>

            {canManage || demoModeActive ? (
              <button
                type="button"
                className="sxp-btn sxp-btn--primary"
                onClick={openCreateModal}
                disabled={demoModeActive || !businessUnitId}
                title={demoModeActive ? demoCopy.readOnly : undefined}
              >
                {extrasCopy.actions.create}
              </button>
            ) : null}
          </div>
        </div>

        <div className="sxp-content">
          {!demoDataUnavailable && <div className="sxp-kpiGrid">
            <article className="sxp-kpiCard sxp-kpiCard--configured">
              <div className="sxp-kpiLabel">{extrasCopy.kpis.totalLabel}</div>
              <div className="sxp-kpiValue">{stats.total}</div>
              <div className="sxp-kpiFoot">{extrasCopy.kpis.totalFoot}</div>
            </article>

            <article className="sxp-kpiCard sxp-kpiCard--automatic">
              <div className="sxp-kpiLabel">{extrasCopy.kpis.automaticLabel}</div>
              <div className="sxp-kpiValue">{stats.automatic}</div>
              <div className="sxp-kpiFoot">{extrasCopy.kpis.automaticFoot}</div>
            </article>

            <article className="sxp-kpiCard sxp-kpiCard--mandatory">
              <div className="sxp-kpiLabel">{extrasCopy.kpis.mandatoryLabel}</div>
              <div className="sxp-kpiValue">{stats.mandatory}</div>
              <div className="sxp-kpiFoot">{extrasCopy.kpis.mandatoryFoot}</div>
            </article>

            <article className="sxp-kpiCard sxp-kpiCard--inclusive">
              <div className="sxp-kpiLabel">{extrasCopy.kpis.inclusiveLabel}</div>
              <div className="sxp-kpiValue">{stats.inclusive}</div>
              <div className="sxp-kpiFoot">{extrasCopy.kpis.inclusiveFoot}</div>
            </article>

            <article className="sxp-kpiCard sxp-kpiCard--dated">
              <div className="sxp-kpiLabel">{extrasCopy.kpis.validityLabel}</div>
              <div className="sxp-kpiValue">{stats.dated}</div>
              <div className="sxp-kpiFoot">{extrasCopy.kpis.validityFoot}</div>
            </article>
          </div>}

          <section className="sxp-filterPanel">
            <div className="sxp-filterTop">
              <span className="sxp-filterLabel">{extrasCopy.filters.quickViewLabel}</span>

              <div className="sxp-pillRow">
                {Object.entries(extrasCopy.options.quickView).map(([key, label]) => (
                  <button
                    key={key}
                    type="button"
                    className={`sxp-pill ${quickViewFilter === key ? "is-active" : ""}`}
                    onClick={() => setQuickViewFilter(key)}
                  >
                    {label}
                  </button>
                ))}
              </div>
            </div>

            <div className="sxp-filterGrid">
              <input
                type="text"
                className="sxp-input"
                value={searchText}
                onChange={(event) => setSearchText(event.target.value)}
                placeholder={extrasCopy.filters.searchPlaceholder}
              />

              <select
                className="sxp-selectNative"
                value={scopeFilter}
                onChange={(event) => setScopeFilter(event.target.value)}
              >
                <option value="all">{extrasCopy.options.scopeFilter.all}</option>
                <option value="per_room">{extrasCopy.options.scopeFilter.per_room}</option>
                <option value="per_reservation">{extrasCopy.options.scopeFilter.per_reservation}</option>
              </select>
            </div>
          </section>

          {!businessUnitError && referencesError ? <div className="sxp-notice">{referencesError}</div> : null}

          <div className="sxp-tableWrap">
            {businessUnitError ? (
              <div className="sxp-state">
                <div>
                  <div className="sxp-stateTitle">{extrasCopy.states.businessUnitErrorTitle}</div>
                  <div className="sxp-stateText">{businessUnitError}</div>
                </div>
              </div>
            ) : loading ? (
              <div className="sxp-state">
                <div>
                  <div className="sxp-stateTitle">{extrasCopy.states.loadingTitle}</div>
                  <div className="sxp-stateText">{extrasCopy.states.loadingText}</div>
                </div>
              </div>
            ) : loadError ? (
              <div className="sxp-state" role="alert">
                <div>
                  <div className="sxp-stateTitle">{extrasCopy.states.loadErrorTitle}</div>
                  <div className="sxp-stateText">{loadError || extrasCopy.states.loadErrorText}</div>
                </div>
              </div>
            ) : filteredExtras.length === 0 ? (
              <div className="sxp-state">
                <div>
                  <div className="sxp-stateTitle">{extrasCopy.states.emptyTitle}</div>
                  <div className="sxp-stateText">{demoModeActive ? demoCopy.empty : extrasCopy.states.emptyText}</div>
                </div>
              </div>
            ) : (
              <table className="sxp-table">
                <thead>
                  <tr>
                    <th>{extrasCopy.table.extra}</th>
                    <th>{extrasCopy.table.pricing}</th>
                    <th>{extrasCopy.table.scope}</th>
                    <th>{extrasCopy.table.targeting}</th>
                    <th>{extrasCopy.table.validity}</th>
                    <th>{extrasCopy.table.flags}</th>
                    <th>{extrasCopy.table.actions}</th>
                  </tr>
                </thead>

                <tbody>
                  {filteredExtras.map((extra) => {
                    const restrictions = formatRestrictions(extra);
                    const linkMeta = getOtaLinkMeta(extra);
                    const categoryTargetIds = normalizeIdArray(extra.room_category_ids);
                    const rateTargetIds = normalizeIdArray(extra.room_rate_ids);
                    const categoryTargetNames = categoryTargetIds.map(
                      (id) => roomCategoryById.get(Number(id))?.name || `#${id}`
                    );
                    const rateTargetNames = rateTargetIds.map((id) => {
                      const rate = roomRateById.get(Number(id));
                      return rate ? buildRateOptionLabel(rate, extrasCopy) : `#${id}`;
                    });
                    const categoryTargetMode = normalizeTargetingMode(
                      extra.room_category_targeting_mode
                    );
                    const rateTargetMode = normalizeTargetingMode(
                      extra.room_rate_targeting_mode
                    );
                    const flags = [
                      extra.booking_engine_visible
                        ? { key: "booking-engine", label: extrasCopy.table.bookingEngine, tone: "brand" }
                        : null,
                      extra.inclusive
                        ? { key: "inclusive", label: extrasCopy.table.inclusive, tone: "connect" }
                        : null,
                      extra.apply_automatically
                        ? { key: "automatic", label: extrasCopy.table.automatic, tone: "horizon" }
                        : null,
                      extra.is_mandatory
                        ? { key: "mandatory", label: extrasCopy.table.mandatory, tone: "mandatory" }
                        : null,
                    ].filter(Boolean);

                    return (
                      <tr key={extra.id} className={`sxp-row${extra.is_active === false ? " sxp-row--inactive" : ""}`}>
                        <td>
                          <div className="sxp-cellTitleRow">
                            {demoModeActive ? (
                              <button type="button" className="sxp-btn sxp-tableBtn sxp-cellTitle"
                                onClick={() => openEditModal(extra, { viewOnly: true })}
                                title={demoCopy.detail}>
                                {extra.name}
                              </button>
                            ) : <div className="sxp-cellTitle">{extra.name}</div>}
                            {demoModeActive ? <span className="sxp-muted">{demoCopy.ota}</span> : <span
                              className={`sxp-linkStatusIcon ${
                                linkMeta.linked ? "sxp-linkStatusIcon--linked" : "sxp-linkStatusIcon--unlinked"
                              }`}
                              title={linkMeta.tooltip}
                              aria-label={linkMeta.label}
                            />}
                          </div>
                          <div className="sxp-cellSub">{extra.description || extrasCopy.table.noDescription}</div>
                        </td>

                        <td>
                          <div className="sxp-stack">
                            <div className="sxp-cellTitle">
                              {formatAmountValue(extra.amount, extra.amount_type, extra.currency, locale, currencyCatalog)}
                            </div>
                            <div className="sxp-inlineMeta">
                              <strong>{extrasCopy.fields.amountType}:</strong>{" "}
                              {extrasCopy.options.amountType[String(extra.amount_type || "absolute")] ||
                                String(extra.amount_type || "-")}
                            </div>
                            <div className="sxp-inlineMeta">
                              <strong>{extrasCopy.fields.chargePeriod}:</strong> {formatChargeLine(extra)}
                            </div>
                          </div>
                        </td>

                        <td>
                          <div className="sxp-stack">
                            <div className="sxp-cellTitle">
                              {extrasCopy.options.scope[String(extra.scope || "per_room")] || String(extra.scope || "-")}
                            </div>
                            <div className="sxp-inlineMeta">
                              <strong>{extrasCopy.table.nights}:</strong> {restrictions.nights}
                            </div>
                            <div className="sxp-inlineMeta">
                              <strong>{extrasCopy.table.pax}:</strong> {restrictions.pax}
                            </div>
                          </div>
                        </td>

                        <td>
                          <div className="sxp-stack">
                            <div>
                              <div className="sxp-muted">{extrasCopy.table.roomCategories}</div>
                              <div className="sxp-cellSub">
                                {categoryTargetNames.length
                                  ? `${
                                      categoryTargetMode === "exclude"
                                        ? extrasCopy.summary.allExcept
                                        : extrasCopy.summary.includeOnly
                                    }: ${categoryTargetNames.join(", ")}`
                                  : extrasCopy.table.allCategories}
                              </div>
                            </div>

                            <div>
                              <div className="sxp-muted">{extrasCopy.table.roomRates}</div>
                              <div className="sxp-cellSub">
                                {rateTargetNames.length
                                  ? `${
                                      rateTargetMode === "exclude"
                                        ? extrasCopy.summary.allExcept
                                        : extrasCopy.summary.includeOnly
                                    }: ${rateTargetNames.join(", ")}`
                                  : extrasCopy.table.allRates}
                              </div>
                            </div>
                          </div>
                        </td>

                        <td>{formatValidityRange(extra.valid_from, extra.valid_to, dateFormatter, extrasCopy)}</td>

                        <td>
                          {flags.length ? (
                            <div className="sxp-flagList">
                              {flags.map((flag) => (
                                <span key={flag.key} className={`sxp-flag sxp-flag--${flag.tone}`}>
                                  {flag.label}
                                </span>
                              ))}
                            </div>
                          ) : (
                            <span className="sxp-muted">{extrasCopy.table.none}</span>
                          )}
                        </td>

                        <td>
                          <div className="sxp-actionRow">
                            {canManage || demoModeActive ? (
                              <>
                                <button
                                  type="button"
                                  className="sxp-btn sxp-tableBtn"
                                  onClick={() => openEditModal(extra)}
                                  disabled={demoModeActive}
                                  title={demoModeActive ? demoCopy.readOnly : undefined}
                                >
                                  {extrasCopy.actions.edit}
                                </button>

                                <button
                                  type="button"
                                  className={`sxp-btn sxp-tableBtn ${extra.is_active ? "sxp-btn--warning" : "sxp-btn--primary"}`}
                                  onClick={() => { void handleToggleActive(extra); }}
                                  disabled={demoModeActive || togglingId === extra.id}
                                  title={demoModeActive ? demoCopy.readOnly : undefined}
                                >
                                  {extra.is_active ? extrasCopy.actions.deactivate : extrasCopy.actions.activate}
                                </button>

                                <button
                                  type="button"
                                  className="sxp-btn sxp-btn--danger sxp-tableBtn"
                                  onClick={() => {
                                    if (demoModeActive || isDemoMode()) return;
                                    setPendingDeleteExtra(extra);
                                  }}
                                  disabled={demoModeActive || Boolean(deletingId)}
                                  title={demoModeActive ? demoCopy.readOnly : undefined}
                                >
                                  {extrasCopy.actions.delete}
                                </button>
                              </>
                            ) : (
                              <span className="sxp-muted">{extrasCopy.actions.viewOnly}</span>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </section>

      {showExtraModal ? (
        <div
          className="sxp-modalOverlay"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) {
              closeModal();
            }
          }}
        >
          <div
            ref={extraModalRef}
            className="sxp-modalShell"
            role="dialog"
            aria-modal="true"
            aria-labelledby="sxp-extra-modal-title"
            aria-describedby="sxp-extra-modal-subtitle"
            tabIndex={-1}
          >
              <div className="sxp-modalHead">
                <div>
                  <p className="sxp-modalKicker">{extrasCopy.modal.kicker}</p>
                  <h2 id="sxp-extra-modal-title" className="sxp-modalTitle">
                    {demoModeActive ? demoCopy.detail : editingExtra ? extrasCopy.modal.editTitle : extrasCopy.modal.createTitle}
                  </h2>
                  {demoModeActive && <span className="sxp-muted">{editingExtra?.is_active ? demoCopy.active : demoCopy.inactive}</span>}
                  <div
                    className={demoModeActive ? "sxp-muted" : `sxp-linkStatusPill ${
                      editingExtraLinkMeta.linked ? "sxp-linkStatusPill--linked" : "sxp-linkStatusPill--unlinked"
                    }`}
                    title={editingExtraLinkMeta.tooltip}
                  >
                    {!demoModeActive && <span
                      className={`sxp-linkStatusIcon ${
                        editingExtraLinkMeta.linked ? "sxp-linkStatusIcon--linked" : "sxp-linkStatusIcon--unlinked"
                      }`}
                      aria-hidden="true"
                    />}
                    <span>{editingExtraLinkMeta.label}</span>
                  </div>
                  <p id="sxp-extra-modal-subtitle" className="sxp-modalSub">{demoModeActive ? demoCopy.readOnly : extrasCopy.modal.subtitle}</p>
                  {demoModeActive && ["currency", "inclusive", "apply_automatically", "is_mandatory", "room_category_ids", "room_rate_ids"].some((field) => editingExtra?.[field] == null) && (
                    <div className="sxp-modalHint">{demoCopy.defaults}</div>
                  )}
                  {!demoModeActive && <div className="sxp-modalHint">{extrasCopy.modal.otaHint}</div>}
                </div>

              <button type="button" className="sxp-closeBtn" onClick={closeModal} aria-label={extrasCopy.actions.close}>
                ×
              </button>
            </div>

            <ModalBody className="sxp-modalBody" disabled={demoModeActive || undefined}
              style={demoModeActive ? { border: 0, margin: 0 } : undefined}
              title={demoModeActive ? demoCopy.readOnly : undefined}>
              <div className="sxp-modalMain">
                <section className="sxp-section">
                  <h3 className="sxp-sectionTitle">{extrasCopy.modal.sections.commercialTitle}</h3>
                  <p className="sxp-sectionText">{extrasCopy.modal.sections.commercialText}</p>

                  <div className="sxp-grid">
                    <div className="sxp-col-7">
                      <label className="sxp-label" htmlFor="sxp-extra-name">
                        {extrasCopy.fields.name}
                      </label>
                      <input
                        id="sxp-extra-name"
                        name="name"
                        type="text"
                        className="sxp-input"
                        value={formData.name}
                        onChange={(event) => handleFormChange("name", event.target.value)}
                        placeholder={extrasCopy.placeholders.name}
                      />
                    </div>

                    <div className="sxp-col-3">
                      <label className="sxp-label">{extrasCopy.fields.amount}</label>
                      <input
                        type="number"
                        step={formData.amount_type === "absolute" ? currencyAmountStep(formData.currency, currencyCatalog) : "0.01"}
                        className="sxp-input"
                        value={formData.amount}
                        onChange={(event) => handleFormChange("amount", event.target.value)}
                      />
                    </div>

                    <div className="sxp-col-2">
                      <label className="sxp-label">{extrasCopy.fields.currency}</label>
                      <select
                        className="sxp-selectNative"
                        value={formData.currency}
                        onChange={(event) => handleFormChange("currency", event.target.value)}
                      >
                        {Array.from(new Set((demoModeActive ? [formData.currency] : [baseCurrency, ...currencies, formData.currency]).filter(Boolean))).map((code) => (
                          <option key={code} value={code}>{code}</option>
                        ))}
                      </select>
                      <div className="sxp-help">{demoModeActive ? demoCopy.currency : extrasCopy.modal.help.currency}</div>
                    </div>

                    <div className="sxp-col-4">
                      <label className="sxp-label">{extrasCopy.fields.amountType}</label>
                      <select
                        className="sxp-selectNative"
                        value={formData.amount_type}
                        onChange={(event) => handleFormChange("amount_type", event.target.value)}
                      >
                        <option value="absolute">{extrasCopy.options.amountType.absolute}</option>
                        <option value="relative">{extrasCopy.options.amountType.relative}</option>
                      </select>
                    </div>

                    <div className="sxp-col-8">
                      <label className="sxp-label">{extrasCopy.fields.description}</label>
                      <textarea
                        className="sxp-textarea"
                        value={formData.description}
                        onChange={(event) => handleFormChange("description", event.target.value)}
                        placeholder={extrasCopy.placeholders.description}
                      />
                    </div>
                  </div>
                </section>

                <section className="sxp-section">
                  <h3 className="sxp-sectionTitle">{extrasCopy.modal.sections.logicTitle}</h3>
                  <p className="sxp-sectionText">{extrasCopy.modal.sections.logicText}</p>

                  <div className="sxp-grid">
                    <div className="sxp-col-4">
                      <label className="sxp-label">{extrasCopy.fields.scope}</label>
                      <select
                        className="sxp-selectNative"
                        value={formData.scope}
                        onChange={(event) => handleFormChange("scope", event.target.value)}
                      >
                        <option value="per_room">{extrasCopy.options.scope.per_room}</option>
                        <option value="per_reservation">{extrasCopy.options.scope.per_reservation}</option>
                      </select>
                    </div>

                    <div className="sxp-col-4">
                      <label className="sxp-label">{extrasCopy.fields.chargePeriod}</label>
                      <select
                        className="sxp-selectNative"
                        value={formData.charge_period}
                        onChange={(event) => handleFormChange("charge_period", event.target.value)}
                      >
                        <option value="per_stay">{extrasCopy.options.chargePeriod.per_stay}</option>
                        <option value="per_night">{extrasCopy.options.chargePeriod.per_night}</option>
                      </select>
                    </div>

                    <div className="sxp-col-4">
                      <label className="sxp-label">{extrasCopy.fields.chargeUnit}</label>
                      {demoModeActive || formData.scope === "per_room" ? (
                        <select
                          className="sxp-selectNative"
                          value={formData.charge_unit}
                          onChange={(event) => handleFormChange("charge_unit", event.target.value)}
                        >
                          <option value="per_room">{extrasCopy.options.chargeUnit.per_room}</option>
                          <option value="per_adult">{extrasCopy.options.chargeUnit.per_adult}</option>
                          <option value="per_child">{extrasCopy.options.chargeUnit.per_child}</option>
                          <option value="per_pax">{extrasCopy.options.chargeUnit.per_pax}</option>
                        </select>
                      ) : (
                        <div className="sxp-helpCard">{extrasCopy.modal.help.reservationScope}</div>
                      )}
                    </div>

                    <div className="sxp-col-6">
                      <label className="sxp-label">{extrasCopy.fields.validFrom}</label>
                      <input
                        type="date"
                        className="sxp-input"
                        value={formData.valid_from}
                        onChange={(event) => handleFormChange("valid_from", event.target.value)}
                      />
                    </div>

                    <div className="sxp-col-6">
                      <label className="sxp-label">{extrasCopy.fields.validTo}</label>
                      <input
                        type="date"
                        className="sxp-input"
                        value={formData.valid_to}
                        onChange={(event) => handleFormChange("valid_to", event.target.value)}
                      />
                    </div>
                  </div>

                  <div className="sxp-help">
                    {formData.scope === "per_reservation"
                      ? extrasCopy.modal.help.reservationScope
                      : extrasCopy.modal.help.roomScope}
                  </div>
                </section>

                <section className="sxp-section">
                  <h3 className="sxp-sectionTitle">{extrasCopy.modal.sections.targetingTitle}</h3>
                  <p className="sxp-sectionText">{extrasCopy.modal.sections.targetingText}</p>

                  <div className="sxp-grid">
                    <div className="sxp-col-6">
                      <label className="sxp-label" htmlFor="extra-room-category-targeting-mode">
                        {extrasCopy.fields.roomCategoryTargetingMode}
                      </label>
                      <select
                        id="extra-room-category-targeting-mode"
                        name="room_category_targeting_mode"
                        className="sxp-selectNative"
                        value={formData.room_category_targeting_mode}
                        onChange={(event) =>
                          handleFormChange("room_category_targeting_mode", event.target.value)
                        }
                      >
                        <option value="include">{extrasCopy.options.targetingMode.include}</option>
                        <option value="exclude">{extrasCopy.options.targetingMode.exclude}</option>
                      </select>

                      <label className="sxp-label sxp-targetSelectorLabel" htmlFor="extra-room-categories">
                        {extrasCopy.fields.roomCategories}
                      </label>
                      <Select
                        inputId="extra-room-categories"
                        isDisabled={demoModeActive}
                        isMulti
                        options={roomCategoryOptions}
                        value={selectedCategoryOptions}
                        onChange={handleCategoryChange}
                        styles={selectStyles}
                        menuPortalTarget={menuPortalTarget}
                        closeMenuOnSelect={false}
                        placeholder={extrasCopy.placeholders.roomCategories}
                        noOptionsMessage={() => extrasCopy.placeholders.noOptions}
                      />
                    </div>

                    <div className="sxp-col-6">
                      <label className="sxp-label" htmlFor="extra-room-rate-targeting-mode">
                        {extrasCopy.fields.roomRateTargetingMode}
                      </label>
                      <select
                        id="extra-room-rate-targeting-mode"
                        name="room_rate_targeting_mode"
                        className="sxp-selectNative"
                        value={formData.room_rate_targeting_mode}
                        onChange={(event) =>
                          handleFormChange("room_rate_targeting_mode", event.target.value)
                        }
                      >
                        <option value="include">{extrasCopy.options.targetingMode.include}</option>
                        <option value="exclude">{extrasCopy.options.targetingMode.exclude}</option>
                      </select>

                      <label className="sxp-label sxp-targetSelectorLabel" htmlFor="extra-room-rates">
                        {extrasCopy.fields.roomRates}
                      </label>
                      <Select
                        inputId="extra-room-rates"
                        isDisabled={demoModeActive}
                        isMulti
                        options={roomRateOptions}
                        value={selectedRateOptions}
                        onChange={handleRateChange}
                        styles={selectStyles}
                        menuPortalTarget={menuPortalTarget}
                        closeMenuOnSelect={false}
                        placeholder={extrasCopy.placeholders.roomRates}
                        noOptionsMessage={() => extrasCopy.placeholders.noOptions}
                      />
                    </div>
                  </div>

                  {referencesError ? (
                    <div className="sxp-help">{extrasCopy.modal.help.referencesError}</div>
                  ) : loading ? (
                    <div className="sxp-help">{extrasCopy.modal.help.referencesLoading}</div>
                  ) : null}
                </section>

                <section className="sxp-section">
                  <h3 className="sxp-sectionTitle">{extrasCopy.modal.sections.restrictionsTitle}</h3>
                  <p className="sxp-sectionText">{demoModeActive ? demoCopy.restrictions : extrasCopy.modal.sections.restrictionsText}</p>

                  <div className="sxp-grid">
                    <div className="sxp-col-3">
                      <label className="sxp-label">{extrasCopy.fields.minNights}</label>
                      <input
                        type="number"
                        className="sxp-input"
                        value={!demoModeActive && formData.charge_period === "per_stay" ? "" : formData.min_nights}
                        onChange={(event) => handleFormChange("min_nights", event.target.value)}
                        disabled={formData.charge_period === "per_stay"}
                      />
                    </div>

                    <div className="sxp-col-3">
                      <label className="sxp-label">{extrasCopy.fields.maxNights}</label>
                      <input
                        type="number"
                        className="sxp-input"
                        value={!demoModeActive && formData.charge_period === "per_stay" ? "" : formData.max_nights}
                        onChange={(event) => handleFormChange("max_nights", event.target.value)}
                        disabled={formData.charge_period === "per_stay"}
                      />
                    </div>

                    <div className="sxp-col-3">
                      <label className="sxp-label">{extrasCopy.fields.minPax}</label>
                      <input
                        type="number"
                        className="sxp-input"
                        value={
                          !demoModeActive && (formData.scope === "per_reservation" || formData.charge_unit === "per_room")
                            ? ""
                            : formData.min_pax
                        }
                        onChange={(event) => handleFormChange("min_pax", event.target.value)}
                        disabled={formData.scope === "per_reservation" || formData.charge_unit === "per_room"}
                      />
                    </div>

                    <div className="sxp-col-3">
                      <label className="sxp-label">{extrasCopy.fields.maxPax}</label>
                      <input
                        type="number"
                        className="sxp-input"
                        value={
                          !demoModeActive && (formData.scope === "per_reservation" || formData.charge_unit === "per_room")
                            ? ""
                            : formData.max_pax
                        }
                        onChange={(event) => handleFormChange("max_pax", event.target.value)}
                        disabled={formData.scope === "per_reservation" || formData.charge_unit === "per_room"}
                      />
                    </div>
                  </div>
                </section>
              </div>

              <aside className="sxp-modalAside">
                <section className="sxp-section">
                  <h3 className="sxp-sectionTitle">{extrasCopy.modal.sections.switchesTitle}</h3>
                  <p className="sxp-sectionText">{extrasCopy.modal.sections.switchesText}</p>

                  <div className="sxp-checkboxGrid">
                    <label className="sxp-checkboxItem">
                      <input
                        type="checkbox"
                        name="booking_engine_visible"
                        checked={formData.booking_engine_visible}
                        onChange={(event) =>
                          handleFormChange("booking_engine_visible", event.target.checked)
                        }
                      />
                      <span className="sxp-checkboxBody">
                        <span className="sxp-checkboxLabel">{extrasCopy.fields.bookingEngineVisible}</span>
                        <span className="sxp-checkboxHint">{extrasCopy.fields.bookingEngineVisibleHint}</span>
                      </span>
                    </label>

                    <label className="sxp-checkboxItem">
                      <input
                        type="checkbox"
                        checked={formData.inclusive}
                        onChange={(event) => handleFormChange("inclusive", event.target.checked)}
                      />
                      <span className="sxp-checkboxBody">
                        <span className="sxp-checkboxLabel">{extrasCopy.fields.inclusive}</span>
                        <span className="sxp-checkboxHint">{extrasCopy.kpis.inclusiveFoot}</span>
                      </span>
                    </label>

                    <label className="sxp-checkboxItem">
                      <input
                        type="checkbox"
                        checked={formData.apply_automatically}
                        onChange={(event) => handleFormChange("apply_automatically", event.target.checked)}
                      />
                      <span className="sxp-checkboxBody">
                        <span className="sxp-checkboxLabel">{extrasCopy.fields.automatic}</span>
                        <span className="sxp-checkboxHint">{extrasCopy.kpis.automaticFoot}</span>
                      </span>
                    </label>

                    <label className="sxp-checkboxItem">
                      <input
                        type="checkbox"
                        checked={formData.is_mandatory}
                        onChange={(event) => handleFormChange("is_mandatory", event.target.checked)}
                      />
                      <span className="sxp-checkboxBody">
                        <span className="sxp-checkboxLabel">{extrasCopy.fields.mandatory}</span>
                        <span className="sxp-checkboxHint">{extrasCopy.kpis.mandatoryFoot}</span>
                      </span>
                    </label>
                  </div>
                </section>

                <section className="sxp-section">
                  <h3 className="sxp-sectionTitle">{extrasCopy.modal.sections.summaryTitle}</h3>
                  <p className="sxp-sectionText">{extrasCopy.modal.sections.summaryText}</p>

                  <div className="sxp-summaryGrid">
                    <div className="sxp-summaryItem">
                      <div className="sxp-summaryLabel">{extrasCopy.summary.amount}</div>
                      <div className="sxp-summaryValue">
                        {formData.amount
                          ? formatAmountValue(formData.amount, formData.amount_type, formData.currency, locale, currencyCatalog)
                          : "-"}
                      </div>
                    </div>

                    <div className="sxp-summaryItem">
                      <div className="sxp-summaryLabel">{extrasCopy.summary.charge}</div>
                      <div className="sxp-summaryValue">
                        {formatChargeLine({
                          charge_period: formData.charge_period,
                          charge_unit: formData.charge_unit,
                          scope: formData.scope,
                        })}
                      </div>
                    </div>

                    <div className="sxp-summaryItem">
                      <div className="sxp-summaryLabel">{extrasCopy.summary.scope}</div>
                      <div className="sxp-summaryValue">
                        {extrasCopy.options.scope[formData.scope] || formData.scope}
                      </div>
                    </div>

                    <div className="sxp-summaryItem">
                      <div className="sxp-summaryLabel">{extrasCopy.summary.validity}</div>
                      <div className="sxp-summaryValue">
                        {formatValidityRange(formData.valid_from, formData.valid_to, dateFormatter, extrasCopy)}
                      </div>
                    </div>

                    <div className="sxp-summaryItem">
                      <div className="sxp-summaryLabel">{extrasCopy.summary.restrictions}</div>
                      <div className="sxp-summaryValue">{summaryRestrictions}</div>
                    </div>

                    <div className="sxp-summaryItem">
                      <div className="sxp-summaryLabel">{extrasCopy.summary.flags}</div>
                      <div className="sxp-summaryValue">
                        {summaryFlags.length ? (
                          <div className="sxp-flagList">
                            {summaryFlags.map((flag) => (
                              <span key={flag.label} className={`sxp-flag sxp-flag--${flag.tone}`}>
                                {flag.label}
                              </span>
                            ))}
                          </div>
                        ) : (
                          extrasCopy.summary.noFlags
                        )}
                      </div>
                    </div>

                    <div className="sxp-summaryItem">
                      <div className="sxp-summaryLabel">{extrasCopy.summary.connection}</div>
                      <div className="sxp-summaryValue">
                        <span
                          className={demoModeActive ? "sxp-muted" : `sxp-linkStatusPill ${
                            editingExtraLinkMeta.linked ? "sxp-linkStatusPill--linked" : "sxp-linkStatusPill--unlinked"
                          }`}
                          title={editingExtraLinkMeta.tooltip}
                        >
                          {!demoModeActive && <span
                            className={`sxp-linkStatusIcon ${
                              editingExtraLinkMeta.linked
                                ? "sxp-linkStatusIcon--linked"
                                : "sxp-linkStatusIcon--unlinked"
                            }`}
                            aria-hidden="true"
                          />}
                          <span>{editingExtraLinkMeta.label}</span>
                        </span>
                      </div>
                    </div>
                  </div>
                </section>

                <section className="sxp-section">
                  <h3 className="sxp-sectionTitle">{extrasCopy.modal.sections.targetsTitle}</h3>
                  <p className="sxp-sectionText">{extrasCopy.modal.sections.targetsText}</p>

                  <div className="sxp-targetPanel">
                    <div className="sxp-targetGroup">
                      <div className="sxp-targetTitle">
                        {extrasCopy.summary.categories} · {formData.room_category_targeting_mode === "exclude"
                          ? extrasCopy.summary.allExcept
                          : extrasCopy.summary.includeOnly}
                      </div>
                      <div className="sxp-targetContent">
                        {selectedCategoryNames.length ? (
                          selectedCategoryNames.map((name) => (
                            <span key={name} className="sxp-chip sxp-chip--horizon">
                              {name}
                            </span>
                          ))
                        ) : (
                          <span className="sxp-emptySlot">{extrasCopy.table.allCategories}</span>
                        )}
                      </div>
                    </div>

                    <div className="sxp-targetGroup">
                      <div className="sxp-targetTitle">
                        {extrasCopy.summary.rates} · {formData.room_rate_targeting_mode === "exclude"
                          ? extrasCopy.summary.allExcept
                          : extrasCopy.summary.includeOnly}
                      </div>
                      <div className="sxp-targetContent">
                        {selectedRateNames.length ? (
                          selectedRateNames.map((name) => <span key={name} className="sxp-chip">{name}</span>)
                        ) : (
                          <span className="sxp-emptySlot">{extrasCopy.table.allRates}</span>
                        )}
                      </div>
                    </div>
                  </div>
                </section>
              </aside>
            </ModalBody>

            <div className="sxp-modalFoot">
              <button type="button" className="sxp-btn" onClick={closeModal} disabled={saving}>
                {extrasCopy.actions.cancel}
              </button>

              <button
                type="button"
                className="sxp-btn sxp-btn--primary"
                onClick={() => {
                  void handleSubmit();
                }}
                disabled={
                  demoModeActive || saving ||
                  !businessUnitId
                }
                title={demoModeActive ? demoCopy.readOnly : undefined}
              >
                {extrasCopy.actions.save}
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {!demoModeActive && pendingDeleteExtra ? (
        <div className="sxp-confirmOverlay">
          <div
            ref={deleteDialogRef}
            className="sxp-confirmDialog"
            role="alertdialog"
            aria-modal="true"
            aria-labelledby="sxp-delete-title"
            aria-describedby="sxp-delete-description"
            tabIndex={-1}
          >
            <div className="sxp-confirmMark" aria-hidden="true">!</div>
            <div className="sxp-confirmContent">
              <p className="sxp-modalKicker">{extrasCopy.header.title}</p>
              <h2 id="sxp-delete-title" className="sxp-confirmTitle">
                {extrasCopy.messages.confirmDelete(pendingDeleteExtra.name || pendingDeleteExtra.id)}
              </h2>
              <p id="sxp-delete-description" className="sxp-confirmText">
                {extrasCopy.messages.deleteDialogHelp}
              </p>
              <div className="sxp-confirmMeta">
                {formatAmountValue(
                  pendingDeleteExtra.amount,
                  pendingDeleteExtra.amount_type,
                  pendingDeleteExtra.currency,
                  locale,
                  currencyCatalog
                )}
              </div>
              <div className="sxp-confirmActions">
                <button
                  type="button"
                  className="sxp-btn"
                  onClick={() => setPendingDeleteExtra(null)}
                  disabled={Boolean(deletingId)}
                >
                  {extrasCopy.actions.cancel}
                </button>
                <button
                  type="button"
                  className="sxp-btn sxp-btn--danger"
                  onClick={() => {
                    void handleDelete(pendingDeleteExtra);
                  }}
                  disabled={Boolean(deletingId)}
                >
                  {deletingId ? extrasCopy.actions.deleting : extrasCopy.actions.delete}
                </button>
              </div>
            </div>
          </div>
        </div>
      ) : null}

      {toast ? (
        <div className="sxp-toastOverlay" role="status" aria-live="polite">
          <div className={`sxp-toastCard sxp-toastCard--${toast.tone}`}>{toast.message}</div>
        </div>
      ) : null}
    </div>
  );
};

export default SettingsExtras;
